import { AxiosInstance, AxiosResponse } from 'axios';

import { AuthEventMetaData, IAuthService } from '@src/features/auth/services/i-auth-service';
import { completeProfileSchema } from '@src/features/profile/components/complete-profile-form/schema';
import { guestApi } from '@src/services';
import { PatchProfileRequest, Profile, ProfileWithGuestId, ProfileWithMetadata } from '@src/types/api/models';
import { ApiResponse } from '@src/types/api/response/api-response';

class ProfileService {
  constructor(
    private networkService: AxiosInstance,
    private authService: IAuthService,
  ) {}

  public async getProfile(): Promise<ProfileWithMetadata | null> {
    const authToken = await this.authService.getAuthToken();

    if (!authToken) {
      return null;
    }

    const [authUser, profileResponse] = await Promise.all([
      this.authService.getAuthUser(),
      this.networkService.get<ApiResponse<ProfileWithGuestId>>('/profile/v1', {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      }),
    ]);

    const profile = profileResponse.data.data;
    let isMandatoryInfoComplete = false;

    if (profile.email) {
      const { data: guestConsentsData } = await guestApi.getGuestConsents({
        params: { email: profile.email },
      });
      isMandatoryInfoComplete = !completeProfileSchema.validate(
        {
          ...profile,
          consents: {
            'general-terms': guestConsentsData.consents.find(({ type }) => type === 'general-terms') ? true : undefined,
            'first-party': guestConsentsData.consents.find(({ type }) => type === 'first-party') ? true : undefined,
            marketing: guestConsentsData.consents.find(({ type }) => type === 'marketing') ? true : undefined,
          },
        },
        {
          abortEarly: false,
          stripUnknown: true,
        },
      ).error;
    }

    return {
      ...profile,
      externalProvider: !!authUser?.externalProvider,
      isMandatoryInfoComplete,
    };
  }

  public async editProfile(oldProfile: Profile, updatedProfile: PatchProfileRequest, metaData: AuthEventMetaData) {
    const oldEmail = oldProfile.unverifiedEmail ?? oldProfile.email;

    if (updatedProfile.email && updatedProfile.email !== oldEmail) {
      await this.authService.updateUserEmail(updatedProfile.email, metaData);
    }

    const authToken = await this.authService.getAuthToken();
    const response = await this.networkService.patch<PatchProfileRequest, AxiosResponse<ApiResponse<Profile>>>(
      `/profile/v1?brandId=${metaData.brandId}`,
      this.sanitize(updatedProfile),
      {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      },
    );

    return response.data.data;
  }

  async confirmProfile(actionCode: string) {
    const authToken = await this.authService.getAuthToken();
    await this.networkService.post(
      '/profile/v1/confirm',
      {
        code: actionCode,
      },
      {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      },
    );
  }

  private sanitize(profile: PatchProfileRequest) {
    return Object.fromEntries(
      Object.entries(profile).map(([key, value]) => {
        return [key, value === '' ? undefined : value];
      }),
    ) as PatchProfileRequest;
  }
}

export default ProfileService;
